import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CourtPackageDownloadDto,
  CourtPackageDto,
  CourtPackageVerificationDto,
  FileCourtPackagesDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export type FileCourtPackages = FileCourtPackagesDto & { canExport: boolean };

export const courtPackageKeys = {
  forFile: (fileId: string) => ['court-packages', fileId] as const,
};

/** A package still being built: the list re-polls until none is. */
export function isBuilding(pkg: Pick<CourtPackageDto, 'status'>): boolean {
  return pkg.status === 'queued' || pkg.status === 'building';
}

export function useCourtPackages(fileId: string) {
  return useQuery({
    queryKey: courtPackageKeys.forFile(fileId),
    queryFn: async () => {
      const { data } = await api.get<FileCourtPackages>(`/files/${fileId}/court-packages`);
      return data;
    },
    enabled: !!fileId,
    // Building happens in the background; poll every few seconds while it does,
    // and not at all once nothing is.
    refetchInterval: (query) => (query.state.data?.packages.some(isBuilding) ? 3000 : false),
  });
}

export function useRequestCourtPackage(fileId: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<CourtPackageDto>(`/files/${fileId}/court-packages`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courtPackageKeys.forFile(fileId) });
      toast.success('gov.courtPackage.requestedToast');
    },
    onError: (err) => toast.error(err, 'gov.courtPackage.requestError'),
  });
}

/**
 * Mint the short-lived link and follow it. The link is minted only at the
 * moment of download, because minting it is what the audit trail records.
 */
export function useDownloadCourtPackage(fileId: string) {
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (packageId: string) => {
      const { data } = await api.get<CourtPackageDownloadDto>(
        `/files/${fileId}/court-packages/${packageId}/download`,
      );
      return data;
    },
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
    onError: (err) => toast.error(err, 'gov.courtPackage.downloadError'),
  });
}

export function useVerifyStoredPackage(fileId: string) {
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (packageId: string) => {
      const { data } = await api.post<CourtPackageVerificationDto>(
        `/files/${fileId}/court-packages/${packageId}/verify`,
      );
      return data;
    },
    onError: (err) => toast.error(err, 'gov.courtPackage.verifyError'),
  });
}

export function useVerifyUploadedPackage(fileId: string) {
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (archive: File) => {
      const form = new FormData();
      form.append('archive', archive);
      const { data } = await api.post<CourtPackageVerificationDto>(
        `/files/${fileId}/court-packages/verify`,
        form,
      );
      return data;
    },
    onError: (err) => toast.error(err, 'gov.courtPackage.verifyError'),
  });
}
